/**
 * cal-core embed widget — entry point.
 *
 * Drop in any site:
 *   <script src="https://cal.bm.consulting/embed.js" defer></script>
 *
 * Button mode (opens modal):
 *   <button data-cal="mj/30min">Reserva</button>
 *
 * Inline mode (embedded iframe in place):
 *   <div data-cal-inline="mj/30min" style="height:680px"></div>
 *
 * Attributes supported on both:
 *   data-cal-theme="dark"|"light"
 *   data-cal-prefill-email="user@x.com"
 *   data-cal-prefill-name="Ana García"
 *   data-cal-locale="es"|"en"
 *   data-cal-color="#1A365D"   (override brand primary color on button/modal)
 *
 * CSP requirement on cal-core side (for embedding on external sites):
 *   Content-Security-Policy: frame-ancestors 'self' https://*.bm.consulting https://*.abemon.es https://*.bmc-vault.com
 *
 * Bundle target: ≤25kb gzipped, zero runtime dependencies.
 */

import { parseDataAttrs, buildIframeSrc } from './parser.js';
import { openBookingModal } from './modal.js';
import { mountInline } from './inline.js';

// ─── Resolve API base URL from <script> src ───────────────────────────────────

function resolveApiBaseUrl(): string {
  // Try to find our own <script> tag by looking for embed.js in src
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]');
  for (const script of Array.from(scripts)) {
    const src = script.src;
    if (src && (src.includes('/embed.js') || src.includes('embed.js'))) {
      try {
        const url = new URL(src);
        return `${url.protocol}//${url.host}`;
      } catch {
        // malformed src — continue
      }
    }
  }
  // Fallback: use current document's host (useful when serving from same origin)
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://localhost:3000';
}

// ─── Core init ───────────────────────────────────────────────────────────────

let apiBaseUrl = '';

function initButtonElement(el: Element): void {
  if (el.hasAttribute('data-cal-listener')) return;
  el.setAttribute('data-cal-listener', '');

  el.addEventListener('click', (e) => {
    e.preventDefault();
    const attrs = parseDataAttrs(el);
    if (!attrs) return;

    const iframeSrc = buildIframeSrc(apiBaseUrl, attrs);

    openBookingModal({
      iframeSrc,
      calOrigin: apiBaseUrl,
      attrs,
      onBooked(bookingId) {
        // Dispatch a custom event on the original element so host pages can react
        el.dispatchEvent(
          new CustomEvent('cal:booked', {
            bubbles: true,
            detail: { bookingId, path: attrs.path },
          }),
        );
      },
    });
  });

  // Keyboard: Enter/Space for non-button elements
  const tag = el.tagName.toLowerCase();
  if (tag !== 'button' && tag !== 'a') {
    (el as HTMLElement).setAttribute('tabindex', '0');
    el.setAttribute('role', 'button');
    el.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        (el as HTMLElement).click();
      }
    });
  }
}

function initInlineElement(el: Element): void {
  const attrs = parseDataAttrs(el);
  if (!attrs) return;
  const iframeSrc = buildIframeSrc(apiBaseUrl, attrs);
  mountInline({
    container: el,
    iframeSrc,
    calOrigin: apiBaseUrl,
    attrs,
    onBooked(bookingId) {
      el.dispatchEvent(
        new CustomEvent('cal:booked', {
          bubbles: true,
          detail: { bookingId, path: attrs.path },
        }),
      );
    },
  });
}

function scanAndBind(): void {
  // Button / anchor mode
  document.querySelectorAll<Element>('[data-cal]').forEach(initButtonElement);
  // Inline mode
  document.querySelectorAll<Element>('[data-cal-inline]').forEach(initInlineElement);
}

// ─── MutationObserver for late-injected elements ──────────────────────────────

function observeDOM(): void {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (!(node instanceof Element)) continue;
        // Check the node itself
        if (node.hasAttribute('data-cal')) initButtonElement(node);
        if (node.hasAttribute('data-cal-inline')) initInlineElement(node);
        // Check descendants
        node.querySelectorAll<Element>('[data-cal]').forEach(initButtonElement);
        node.querySelectorAll<Element>('[data-cal-inline]').forEach(initInlineElement);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Bootstrap ───────────────────────────────────────────────────────────────

function bootstrap(): void {
  apiBaseUrl = resolveApiBaseUrl();
  scanAndBind();
  observeDOM();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// ─── Public API (optional programmatic usage) ─────────────────────────────────
// Allows: CalEmbed.open('mj/30min', { theme: 'dark' })

declare global {
  interface Window {
    CalEmbed?: typeof CalEmbed;
  }
}

const CalEmbed = {
  open(path: string, options?: { theme?: 'dark' | 'light'; locale?: string; prefillEmail?: string; prefillName?: string; color?: string }) {
    const attrs = {
      path,
      theme: options?.theme,
      locale: options?.locale,
      prefillEmail: options?.prefillEmail,
      prefillName: options?.prefillName,
      color: options?.color,
    };
    const iframeSrc = buildIframeSrc(apiBaseUrl, attrs);
    openBookingModal({ iframeSrc, calOrigin: apiBaseUrl, attrs });
  },
  /** Force a re-scan (call after dynamic DOM changes if MutationObserver isn't sufficient). */
  scan(): void {
    scanAndBind();
  },
};

if (typeof window !== 'undefined') {
  window.CalEmbed = CalEmbed;
}

export { CalEmbed };
